import React from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';
import type { AvatarProps } from '@atlaskit/avatar-group/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';

import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes.ts';
import { MAX_GROUP_SIZE } from '../presenceUtils.ts';
import { useScrollToTelepointer } from '../useScrollToTelepointer.ts';

import { PresenceAvatar } from './PresenceAvatar.tsx';

type PresenceAvatarProps = {
	index: number;
	participant: Participant;
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	isSingleParticipant: boolean;
	isEligibleForEmptyPresenceDialog: boolean;
	toggleEmptyPresenceDialog: () => void;
	avatarProps: AvatarProps;
	analyticSource?: string;
};

export const PresenceAvatarButton = ({
	index,
	participant,
	contentMode,
	contentType,
	contentId,
	isSingleParticipant,
	isEligibleForEmptyPresenceDialog,
	toggleEmptyPresenceDialog,
	avatarProps,
	analyticSource,
}: PresenceAvatarProps): React.JSX.Element => {
	const { id } = participant;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isScrollToFeatureEnabled, isEligibleToScroll, scrollToTelepointer } =
		useScrollToTelepointer({
			contentType,
			contentMode,
			// TODO: https://product-fabric.atlassian.net/browse/MDS-1320
			participant: participant as any,
		});

	const getAvatarButtonOnClickHandler = () => {
		if (isSingleParticipant) {
			return isEligibleForEmptyPresenceDialog ? toggleEmptyPresenceDialog : undefined;
		}

		if (isEligibleToScroll) {
			return () => {
				const isScrollSuccessful = scrollToTelepointer();

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: analyticSource,
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'presenceAvatarButton',
						product: 'confluence',
						attributes: {
							contentId,
							contentType,
							contentMode,
							isScrollToFeatureEnabled,
							isEligibleToScroll,
							isScrollSuccessful,
						},
					},
				}).fire();
			};
		}

		return participant?.onClick;
	};

	return (
		<Box
			padding={fg('platform-avatar-group-spacing-fix') ? 'space.0' : 'space.025'}
			key={`box-${id}`}
			data-vc="presence-avatar"
		>
			<PresenceAvatar
				{...avatarProps}
				participant={participant}
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				key={`avatar-${id}`}
				stackIndex={MAX_GROUP_SIZE - index}
				testId={`team-presence-avatar-${index}`}
				onClick={getAvatarButtonOnClickHandler()}
				isEligibleToScroll={isEligibleToScroll}
				isScrollToFeatureEnabled={isScrollToFeatureEnabled}
			/>
		</Box>
	);
};
